/**
 * Change Accounts Modal
 *
 * Opens from .rs-profile-locations-container__change-accounts (profile locations page).
 *
 * Flow:
 * 1) Account list — search filters companies once the query is 2+ characters (same idea as the main profile search).
 * 2) If the account has locations, a location list appears (modal title switches to the location-step string from locales, e.g. "Choose a Location") with the same style of search as accounts (2+ chars: filter by location id, address, ZIP).
 * 3) Clicking a location commits immediately — cookies, setLocationCookie, POST
 *    /api/company/assign-company-locations, updateLocationOnShopify, reload. An in-modal overlay shows while that runs.
 *
 * Globals from rs-location-picker.js (when loaded): setLocationCookie, onContextChangeClear,
 * updateLocationOnShopify, waitForShopifyB2BSessionAfterContextChange, announce.
 */
import {removeTrapFocus, trapFocus} from '@theme/focus';
import {getCustomerAppBaseUrl} from '@theme/gadget-envs';

/**
 * Assign the selected company location via Gadget company/assign-company-locations.
 * @param {string} companyId
 * @param {string} [locationId]
 * @returns {Promise<boolean>}
 */
async function assignCompanyLocations(companyId, locationId) {
  if (companyId == null || companyId === '') return false;

  /** @type {{ company_id: string, location_id?: string, location_ids?: string[] }} */
  const payload = {company_id: companyId};
  if (locationId) {
    payload.location_id = locationId;
    payload.location_ids = [locationId];
  }

  try {
    const res = await fetch(`${getCustomerAppBaseUrl()}/api/customer/assign-company-location`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${window.myJWT}`,
      },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      console.error('Error assigning company locations:', data);
      const readError = Reflect.get(window, 'readAssignCompanyLocationError');
      const showError = Reflect.get(window, 'setPickerContextSwitchError');
      if (typeof showError === 'function') {
        showError(
          typeof readError === 'function'
            ? readError(data, res)
            : `Request failed (${res.status}).`
        );

          // Close the location picker modal
          const locationPickerModal = document.querySelector('.rs-location-picker-modal');
          if (locationPickerModal) {
            locationPickerModal.classList.add('rs-hidden');
            locationPickerModal.setAttribute('aria-hidden', 'true');
          }
      }
      if (typeof announce === 'function') {
        announce('There was an error assigning the company.');
      }
      return false;
    }
    return true;
  } catch (e) {
    if (/** @type {{ name?: string }} */ (e).name === 'AbortError') throw e;
    console.error('Network error assigning company locations', e);
    const showError = Reflect.get(window, 'setPickerContextSwitchError');
    if (typeof showError === 'function') {
      showError('Network error.');
      // Close the location picker modal
      const locationPickerModal = document.querySelector('.rs-location-picker-modal');
      if (locationPickerModal) {
        locationPickerModal.classList.add('rs-hidden');
        locationPickerModal.setAttribute('aria-hidden', 'true');
      }
    }
    if (typeof announce === 'function') {
      announce('Network error assigning the company.');
    }
    return false;
  }
}

document.addEventListener('DOMContentLoaded', function () {
  // --- DOM ---
  const changeAccountsButton = document.querySelector('.rs-profile-locations-container__change-accounts');
  const changeAccountsModal = document.querySelector('.rs-profile-change-accounts-modal');
  const closeButton = document.querySelector('.rs-profile-change-accounts-modal__close');
  const stepAccount = document.querySelector('.rs-profile-change-accounts-modal__step--account');
  const stepLocation = document.querySelector('.rs-profile-change-accounts-modal__step--location');
  const searchInput = changeAccountsModal?.querySelector('.rs-profile-change-accounts-modal__step--account .rs-location-dropdown__search-wrapper input.rs-input');
  const accountsList = stepAccount.querySelector('.rs-profile-change-accounts-modal__accounts-list');
  const locationsList = stepLocation.querySelector('.rs-profile-change-accounts-modal__locations-list');
  const locationIntroEl = stepLocation.querySelector('.rs-profile-change-accounts-modal__location-intro');
  const locationSearchInput = stepLocation.querySelector('.rs-profile-change-accounts-modal__location-search input.rs-input');
  const applyingOverlay = changeAccountsModal?.querySelector('.rs-profile-change-accounts-modal__applying-overlay');
  const modalTitleEl = changeAccountsModal?.querySelector('.rs-modal__title');

  if (!changeAccountsButton || !changeAccountsModal || !accountsList || !stepAccount || !stepLocation || !locationsList) return;

  // Copy comes from Liquid via data-* (Theme Editor translations / en.default.json).
  const noAccountsFoundText = changeAccountsModal.getAttribute('data-no-accounts-found')?.trim() || 'No accounts found.';
  const loadingAccountsText = changeAccountsModal.getAttribute('data-loading-accounts')?.trim() || 'Loading accounts...';
  const locationIntroText = changeAccountsModal.getAttribute('data-location-intro')?.trim() || '';
  const noLocationsText = changeAccountsModal.getAttribute('data-no-locations')?.trim() || 'This account has no locations.';
  const currentlySelectedText = changeAccountsModal.getAttribute('data-currently-selected')?.trim() || 'Currently selected';
  const selectLabelText = changeAccountsModal.getAttribute('data-select-label')?.trim() || 'Select';
  const changeLocationLabelText = changeAccountsModal.getAttribute('data-change-location-label')?.trim() || 'Change location';
  const noLocationsFoundText = changeAccountsModal.getAttribute('data-no-locations-found')?.trim() || 'No locations found.';
  const locationStepTitleText = changeAccountsModal.getAttribute('data-location-step-title')?.trim() || 'Choose a Location';

  let lastAccountFilter = '';
  /** Caches the h2 from Liquid (block account title) before we overwrite it on the location step. */
  let accountStepTitleCache = '';
  /** Company whose locations are shown in step 2 (used when filtering by location search). */
  let locationStepCompany = null;
  /** Prevents double submits if the user clicks again before reload. */
  let applying = false;
  /** At most one reload() — same idea as rs-location-picker.js schedulePickerReload. */
  let changeAccountsReloadScheduled = false;
  /** @type {HTMLElement | null} */
  let previousActiveElement = null;
  /** @type {HTMLElement[]} */
  let inertedChrome = [];
  let modalKeydownListening = false;
  const mainContent = document.querySelector('main');

  /**
   * @param {HTMLElement | null | undefined} modal
   * @returns {HTMLElement | null}
   */
  function getModalFocusContainer(modal) {
    if (!(modal instanceof HTMLElement)) return null;
    const content = modal.querySelector('.rs-modal__content');
    return content instanceof HTMLElement ? content : modal;
  }

  function clearPageInertBehindModal() {
    inertedChrome.forEach((node) => node.removeAttribute('inert'));
    inertedChrome = [];
  }

  /** @param {HTMLElement} modal */
  function setPageInertBehindModal(modal) {
    clearPageInertBehindModal();
    for (const node of document.body.children) {
      if (!(node instanceof HTMLElement)) continue;
      if (node === modal || node.contains(modal)) continue;
      node.setAttribute('inert', '');
      inertedChrome.push(node);
    }
  }

  /**
   * @param {HTMLElement} modal
   * @param {{ initialFocus?: HTMLElement | null }} [options]
   */
  function scheduleModalFocusTrap(modal, options = {}) {
    const container = getModalFocusContainer(modal);
    if (!container) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (changeAccountsModal.classList.contains('rs-hidden')) return;

        trapFocus(container, {
          initialFocus:
            options.initialFocus instanceof HTMLElement ? options.initialFocus : undefined,
        });
      });
    });
  }

  function releaseModalFocusTrap() {
    removeTrapFocus();

    if (modalKeydownListening) {
      document.removeEventListener('keydown', handleModalKeydown);
      modalKeydownListening = false;
    }

    if (mainContent instanceof HTMLElement) {
      mainContent.setAttribute('aria-hidden', 'false');
    }

    clearPageInertBehindModal();
  }

  /**
   * @param {HTMLElement} modal
   * @param {{ initialFocus?: HTMLElement | null }} [options]
   */
  function activateModalFocusTrap(modal, options = {}) {
    if (!(modal instanceof HTMLElement)) return;

    if (mainContent instanceof HTMLElement) {
      mainContent.setAttribute('aria-hidden', 'true');
    }
    modal.setAttribute('aria-hidden', 'false');

    if (!modalKeydownListening) {
      document.addEventListener('keydown', handleModalKeydown);
      modalKeydownListening = true;
    }

    setPageInertBehindModal(modal);
    scheduleModalFocusTrap(modal, options);
  }

  /** @param {KeyboardEvent} event */
  function handleModalKeydown(event) {
    if (event.key !== 'Escape') return;
    if (changeAccountsModal.classList.contains('rs-hidden')) return;
    if (applying) return;

    event.preventDefault();
    closeModal();
  }

  function scheduleChangeAccountsReload() {
    if (changeAccountsReloadScheduled) return;
    changeAccountsReloadScheduled = true;
    try {
      sessionStorage.setItem('rs_b2b_context_navigation_at', String(Date.now()));
    } catch (e) {}
    window.location.reload();
  }

  function setApplyingUi(show) {
    if (!applyingOverlay) return;
    if (show) {
      applyingOverlay.classList.remove('rs-hidden');
      applyingOverlay.setAttribute('aria-hidden', 'false');
      changeAccountsModal.setAttribute('aria-busy', 'true');
    } else {
      applyingOverlay.classList.add('rs-hidden');
      applyingOverlay.setAttribute('aria-hidden', 'true');
      changeAccountsModal.removeAttribute('aria-busy');
    }
  }

  function disableLocationButtons(disabled) {
    locationsList.querySelectorAll('.rs-profile-change-accounts-modal__location-card').forEach((btn) => {
      btn.disabled = disabled;
    });
  }

  // --- Address / ID helpers (align with rs-profile-locations.js / rs-location-picker.js) ---

  function formatFullAddress(addr) {
    if (!addr) return '';
    const parts = [addr.address1, addr.address2, [addr.city, addr.zoneCode || addr.provinceCode || addr.province].filter(Boolean).join(', '), addr.zip || addr.postalCode, addr.countryCode || addr.country].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : addr.formattedArea || '';
  }

  /** Numeric id string used in location_id cookie and URLs (strips gid://shopify/CompanyLocation/). */
  function locFormattedFromGid(gid) {
    if (!gid) return '';
    return String(gid).replace(/^gid:\/\/shopify\/CompanyLocation\//, '');
  }

  /** Prefer company_id cookie; Liquid sets window.currentCompanyId as fallback. */
  const getCurrentCompanyId = () =>
    document.cookie
      .split('; ')
      .find((r) => r.startsWith('company_id='))
      ?.split('=')[1] || String(window.currentCompanyId || '');

  /** Compare company ids whether they are numeric or full Shopify Company GIDs. */
  const normalizeCompanyId = (id) => {
    if (id == null || id === '') return '';
    const s = String(id).trim();
    const gidMatch = s.match(/Company\/([^/?]+)/i);
    if (gidMatch) return gidMatch[1];
    if (/^\d+$/.test(s)) return s;
    const digitsMatch = s.match(/(\d+)/);
    return digitsMatch ? digitsMatch[1] : s;
  };

  /** Safe insertion of user/translated strings into innerHTML templates. */
  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  /** Account + location list search: require this many characters before filtering (matches profile locations UX). */
  const MIN_SEARCH_CHARS = 2;

  /** Normalize window.companies_data whether it is { companies: [] }, a bare array, or a map of arrays. */
  const getCompanies = () => {
    if (!window.companies_data) return [];
    if (Array.isArray(window.companies_data?.companies)) return window.companies_data.companies;
    if (Array.isArray(window.companies_data)) return window.companies_data;

    return Object.values(window.companies_data).reduce((acc, value) => {
      if (Array.isArray(value)) acc.push(...value);
      return acc;
    }, []);
  };

  /** Secondary line on account cards: external id or first location address. */
  const getCompanySubtext = (company) => {
    if (company?.company_external_id) return `#${company.company_external_id}`;
    const firstAddress = formatFullAddress(company?.locations?.[0]?.shippingAddress);
    return firstAddress || '';
  };

  function syncAccountStepModalTitle() {
    if (!modalTitleEl) return;
    if (accountStepTitleCache === '') {
      accountStepTitleCache = modalTitleEl.textContent.trim();
    }
    if (accountStepTitleCache) {
      modalTitleEl.textContent = accountStepTitleCache;
    }
  }

  function syncLocationStepModalTitle() {
    if (!modalTitleEl) return;
    if (accountStepTitleCache === '') {
      accountStepTitleCache = modalTitleEl.textContent.trim();
    }
    modalTitleEl.textContent = locationStepTitleText;
  }

  /** Step 1: show account search + list (closing the modal also resets here). */
  function showAccountStep() {
    locationStepCompany = null;
    stepLocation.classList.add('rs-hidden');
    stepAccount.classList.remove('rs-hidden');
    if (locationSearchInput) locationSearchInput.value = '';
    syncAccountStepModalTitle();
  }

  /**
   * Switch company when there are zero locations — matches org-picker branch that clears location_id / context.
   * No second step; commits immediately (reload).
   */
  async function applyCompanyNoLocations(company) {
    if (applying) return;
    applying = true;
    setApplyingUi(true);
    const id = company.company_id;
    const externalId = company.company_external_id || '';
    const name = company.company_name || '';

    if (typeof clearAdminDefaultLocationAssignedFlag === 'function') {
      clearAdminDefaultLocationAssignedFlag();
    }
    if (typeof rememberPendingCompanySwitch === 'function') {
      rememberPendingCompanySwitch(id, externalId);
    }
    document.cookie = `location_id=; path=/`;
    document.cookie = `context=; path=/; max-age=0`;
    document.cookie = `company_id=${id}; path=/`;
    const msg = `Now viewing ${name} (#${externalId})`;
    document.cookie = `show_now_viewing_company_popup=${encodeURIComponent(msg)}; path=/`;

    // Do not call initializeCoveoConfig() here — it can trigger location.reload() (e.g. Coveo token path)
    // before we finish; full reload below refreshes Coveo with new cookies.

    if (document.body.dataset.cartProductNumbers !== '[]' && typeof onContextChangeClear === 'function') {
      await onContextChangeClear();
    }

    if (typeof announce === 'function') {
      announce(msg);
    }
    let assigned = true;
    if (typeof assignCompanyLocation === 'function') {
      try {
        assigned = await assignCompanyLocation(id);
      } catch (e) {
        if (e.name === 'AbortError') {
          applying = false;
          setApplyingUi(false);
          return;
        }
        throw e;
      }
    }
    if (!assigned) {
      applying = false;
      setApplyingUi(false);
      const showError = Reflect.get(window, 'setPickerContextSwitchError');
      const getLastError = Reflect.get(window, 'getLastAssignCompanyLocationError');
      if (typeof showError === 'function') {
        showError(typeof getLastError === 'function' ? getLastError() : '');
      }
      return;
    }
    const waitOpts = {expectedCompanyId: id, expectedCompanyExternalId: externalId};
    if (typeof completeContextChangeAndNavigate === 'function') {
      const ok = await completeContextChangeAndNavigate(scheduleChangeAccountsReload, waitOpts);
      if (!ok) {
        applying = false;
        setApplyingUi(false);
      }
      return;
    }
    await new Promise((r) => setTimeout(r, 2000));
    scheduleChangeAccountsReload();
  }

  /**
   * Persist company + location and reload. Order and side effects kept in sync with rs-location-picker.js org/location clicks.
   */
  async function applyCompanyAndLocation(company, locGid) {
    if (applying) return;
    applying = true;
    setApplyingUi(true);
    disableLocationButtons(true);
    const id = company.company_id;
    const externalId = company.company_external_id || '';
    const name = company.company_name || '';
    const locFormatted = locFormattedFromGid(locGid);

    if (typeof clearAdminDefaultLocationAssignedFlag === 'function') {
      clearAdminDefaultLocationAssignedFlag();
    }
    if (typeof rememberPendingCompanySwitch === 'function') {
      rememberPendingCompanySwitch(id, externalId);
    }
    document.cookie = `company_id=${id}; path=/`;
    const msg = `Now viewing ${name} (#${externalId})`;
    document.cookie = `show_now_viewing_company_popup=${encodeURIComponent(msg)}; path=/`;

    document.cookie = `location_id=${locFormatted}; path=/`;
    if (typeof setLocationCookie === 'function') {
      setLocationCookie(company, locGid);
    }

    const locRow = (company.locations || []).find((l) => String(l.location_id) === String(locGid));
    const expectedShipTo = locRow?.ship_to_id;

    // Do not call initializeCoveoConfig() — same double-reload issue as applyCompanyNoLocations / rs-location-picker.

    if (document.body.dataset.cartProductNumbers !== '[]' && typeof onContextChangeClear === 'function') {
      await onContextChangeClear();
    }

    if (typeof announce === 'function') {
      announce(msg);
    }

    let assigned = true;
    try {
      assigned = await assignCompanyLocations(id, locGid);
    } catch (e) {
      if (e.name === 'AbortError') {
        applying = false;
        setApplyingUi(false);
        disableLocationButtons(false);
        return;
      }
      throw e;
    }
    if (!assigned) {
      applying = false;
      setApplyingUi(false);
      disableLocationButtons(false);
      return;
    }
    if (typeof updateLocationOnShopify === 'function') {
      const locOk = await updateLocationOnShopify(locFormatted);
      if (!locOk) {
        applying = false;
        setApplyingUi(false);
        disableLocationButtons(false);
        return;
      }
    }
    const waitOpts = {
      expectedCompanyId: id,
      expectedCompanyExternalId: externalId,
      expectedLocationFormatted: locFormatted,
      expectedShipTo,
    };
    if (typeof completeContextChangeAndNavigate === 'function') {
      const ok = await completeContextChangeAndNavigate(scheduleChangeAccountsReload, waitOpts);
      if (!ok) {
        applying = false;
        setApplyingUi(false);
        disableLocationButtons(false);
      }
      return;
    }
    await new Promise((r) => setTimeout(r, 2000));
    scheduleChangeAccountsReload();
  }

  /**
   * Same behavior as profile / header location search: match location #, address, numeric id / ZIP fragments.
   */
  function filterLocationsByQuery(locations, filterQuery) {
    const query = String(filterQuery || '').trim().toLowerCase();
    if (query.length < MIN_SEARCH_CHARS) return locations;

    const normalize = (s) => (s || '').toString().trim().toLowerCase();
    const digitsOnly = (s) => (s || '').replace(/\D+/g, '');
    const extractZip = (s) => {
      const m = (s || '').match(/\b\d{5}(?:-\d{4})?\b/i);
      return m ? m[0] : '';
    };
    const qDigits = digitsOnly(query);

    return locations.filter((loc) => {
      const locGid = loc.location_id || loc.id;
      const formatted = locFormattedFromGid(locGid);
      const extId = String(loc.ship_to_id || '');
      const addr = formatFullAddress(loc?.shippingAddress) || loc?.shippingAddress?.formattedArea || '';
      const idTextNorm = normalize(`Location ${extId}`);
      const addrNorm = normalize(addr);
      const zip = extractZip(addr).toLowerCase();
      const idDigits = digitsOnly(formatted);
      const extDigits = digitsOnly(extId);

      return (
        idTextNorm.includes(query) ||
        addrNorm.includes(query) ||
        normalize(formatted).includes(query) ||
        (qDigits && (idDigits.includes(qDigits) || extDigits.includes(qDigits))) ||
        (zip && zip.includes(query))
      );
    });
  }

  /**
   * Renders the location list for step 2, optionally filtered. Current location (cookie + company) gets rs-active + badge.
   */
  function renderLocationRows(company, filterQuery = '') {
    const locations = company?.locations || [];
    if (!locations.length) {
      locationsList.innerHTML = `<p class="text-medium weight-500 text-brand-black" style="padding: 8px 0;">${escapeHtml(noLocationsText)}</p>`;
      return;
    }

    const filtered = filterLocationsByQuery(locations, filterQuery);
    if (!filtered.length) {
      locationsList.innerHTML = `<p class="text-medium weight-500 text-brand-black" style="padding: 8px 0;">${escapeHtml(noLocationsFoundText)}</p>`;
      return;
    }

    const cookieLoc =
      document.cookie
        .split('; ')
        .find((r) => r.startsWith('location_id='))
        ?.split('=')[1] || '';
    const currentCo = normalizeCompanyId(getCurrentCompanyId());
    const thisCo = normalizeCompanyId(company.company_id);
    const isSameCompany = currentCo === thisCo;

    locationsList.innerHTML = filtered
      .map((loc) => {
        const locGid = loc.location_id || loc.id;
        const formatted = locFormattedFromGid(locGid);
        const extId = loc.ship_to_id || '';
        const addr = formatFullAddress(loc?.shippingAddress) || loc?.shippingAddress?.formattedArea || '';
        const isSelected = isSameCompany && cookieLoc && formatted === cookieLoc;
        const locationLabel = [`Location #${extId}`, addr, isSelected ? currentlySelectedText : ''].filter(Boolean).join(', ');
        const badgeHtml = isSelected
          ? `<span class="rs-profile-change-accounts-modal__current-badge" aria-hidden="true">${escapeHtml(currentlySelectedText)}</span>`
          : '';
        return `
          <button type="button" class="rs-profile-change-accounts-modal__location-card ${isSelected ? 'rs-active' : ''}" data-location-id="${escapeHtml(String(locGid))}" aria-label="${escapeHtml(locationLabel)}">
            <div class="rs-profile-change-accounts-modal__location-text">
              <p class="rs-profile-change-accounts-modal__location-title text-medium weight-500" aria-hidden="true">Location #${escapeHtml(String(extId))}</p>
              <p class="rs-profile-change-accounts-modal__location-address text-small weight-400" aria-hidden="true">${escapeHtml(addr)}</p>
            </div>
            ${badgeHtml}
          </button>`;
      })
      .join('');

    locationsList.querySelectorAll('.rs-profile-change-accounts-modal__location-card').forEach((btn) => {
      btn.addEventListener('click', () => {
        const locGid = btn.getAttribute('data-location-id');
        if (!locGid) return;
        void applyCompanyAndLocation(company, locGid);
      });
    });
  }

  /**
   * Step 2: intro + search + locations for the chosen company. A row click runs the same switch as rs-location-picker.
   */
  function showLocationStep(company) {
    locationStepCompany = company;
    stepAccount.classList.add('rs-hidden');
    stepLocation.classList.remove('rs-hidden');
    syncLocationStepModalTitle();
    if (locationIntroEl) {
      locationIntroEl.textContent = locationIntroText;
    }
    if (locationSearchInput) {
      locationSearchInput.value = '';
    }
    renderLocationRows(company, '');
    scheduleModalFocusTrap(changeAccountsModal, {
      initialFocus: locationSearchInput instanceof HTMLElement ? locationSearchInput : null,
    });
  }

  /** Resolve full company object (including locations[]) from companies_data. */
  function findCompanyById(companyId) {
    return getCompanies().find((c) => String(c.company_id) === String(companyId)) || null;
  }

  /**
   * Renders filtered account cards and wires Select / Change location.
   * Search filters when query length >= 2 (empty or 1 char shows full list).
   */
  const renderAccountRows = (filterQuery = '') => {
    lastAccountFilter = filterQuery;
    const query = String(filterQuery || '').trim().toLowerCase();
    const currentCompanyId = normalizeCompanyId(getCurrentCompanyId());
    const companies = getCompanies();
    const filtered =
      query.length < MIN_SEARCH_CHARS
        ? companies
        : companies.filter((company) => {
            const name = String(company?.company_name || '').toLowerCase();
            const externalId = String(company?.company_external_id || '').toLowerCase();
            const subtext = getCompanySubtext(company).toLowerCase();
            return name.includes(query) || externalId.includes(query) || subtext.includes(query);
          });

    if (!filtered.length) {
      accountsList.innerHTML = `<p class="text-medium weight-500 text-brand-black" style="padding: 8px 0;">${escapeHtml(noAccountsFoundText)}</p>`;
      return;
    }

    accountsList.innerHTML = filtered
      .map((company) => {
        const companyId = String(company?.company_id || '');
        const isCurrent = normalizeCompanyId(companyId) === currentCompanyId;
        const name = company?.company_name || 'Account';
        const subtext = getCompanySubtext(company);
        const locs = company?.locations || [];

        // Current account: badge only, or badge + "Change location" if multiple locations under this company.
        let rightCol = '';
        if (isCurrent) {
          if (locs.length > 1) {
            rightCol = `
              <div class="rs-profile-change-accounts-modal__account-actions">
                <span class="rs-profile-change-accounts-modal__current-badge">${escapeHtml(currentlySelectedText)}</span>
                <button type="button" class="rs-button rs-button-link rs-profile-change-accounts-modal__change-location-action" aria-label="${escapeHtml(changeLocationLabelText + ': ' + name)}">${escapeHtml(changeLocationLabelText)}</button>
              </div>`;
          } else {
            rightCol = `<span class="rs-profile-change-accounts-modal__current-badge" aria-label="${escapeHtml(currentlySelectedText + ': ' + name)}">${escapeHtml(currentlySelectedText)}</span>`;
          }
        } else {
          rightCol = `<button type="button" class="rs-button rs-button-link rs-profile-change-accounts-modal__account-action" aria-label="${escapeHtml(selectLabelText + ': ' + name)}">${escapeHtml(selectLabelText)}</button>`;
        }

        return `
          <div class="rs-profile-change-accounts-modal__account-card ${isCurrent ? 'rs-active' : ''}" data-company-id="${escapeHtml(companyId)}" role="group" aria-label="${escapeHtml(name)}">
            <div class="rs-profile-change-accounts-modal__account-text">
              <p class="rs-profile-change-accounts-modal__account-title text-medium weight-500">${escapeHtml(name)}</p>
              <p class="rs-profile-change-accounts-modal__account-subtext text-small weight-400 text-placeholder">${escapeHtml(subtext)}</p>
            </div>
            ${rightCol}
          </div>
        `;
      })
      .join('');

    accountsList.querySelectorAll('.rs-profile-change-accounts-modal__account-action').forEach((button) => {
      button.addEventListener('click', (event) => {
        const card = event.currentTarget.closest('.rs-profile-change-accounts-modal__account-card');
        const companyId = card?.getAttribute('data-company-id');
        if (!companyId) return;
        const company = findCompanyById(companyId);
        if (!company) return;
        const locs = company.locations || [];
        if (locs.length === 0) {
          void applyCompanyNoLocations(company);
          return;
        }
        showLocationStep(company);
      });
    });

    accountsList.querySelectorAll('.rs-profile-change-accounts-modal__change-location-action').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const card = event.currentTarget.closest('.rs-profile-change-accounts-modal__account-card');
        const companyId = card?.getAttribute('data-company-id');
        if (!companyId) return;
        const company = findCompanyById(companyId);
        if (!company) return;
        showLocationStep(company);
      });
    });
  };

  /** companies_data is often filled async after first paint; poll briefly then render (or empty state). */
  const waitForCompaniesAndRender = (attempt = 0) => {
    if (window.companies_data) {
      renderAccountRows(lastAccountFilter);
      return;
    }
    if (attempt > 20) {
      renderAccountRows(lastAccountFilter);
      return;
    }
    accountsList.innerHTML = `<p class="text-medium weight-500 text-brand-black" style="padding: 8px 0;">${escapeHtml(loadingAccountsText)}</p>`;
    setTimeout(() => waitForCompaniesAndRender(attempt + 1), 150);
  };

  const openModal = () => {
    // Always start on account step; clear search filter state for a fresh open.
    applying = false;
    setApplyingUi(false);
    previousActiveElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    changeAccountsModal.classList.remove('rs-hidden');
    changeAccountsModal.setAttribute('aria-hidden', 'false');
    showAccountStep();
    lastAccountFilter = '';
    waitForCompaniesAndRender();

    if (searchInput instanceof HTMLInputElement) {
      searchInput.value = '';
    }

    activateModalFocusTrap(changeAccountsModal, {
      initialFocus: searchInput instanceof HTMLElement ? searchInput : null,
    });
  };

  const closeModal = () => {
    if (applying) return;

    releaseModalFocusTrap();
    changeAccountsModal.classList.add('rs-hidden');
    changeAccountsModal.setAttribute('aria-hidden', 'true');
    showAccountStep();

    if (searchInput instanceof HTMLInputElement) {
      searchInput.value = '';
    }

    if (previousActiveElement instanceof HTMLElement) {
      previousActiveElement.focus();
      previousActiveElement = null;
    } else if (changeAccountsButton instanceof HTMLElement) {
      changeAccountsButton.focus();
    }
  };

  Reflect.set(window, 'closeChangeAccountsModal', closeModal);

  // --- Events ---

  changeAccountsButton.addEventListener('click', (event) => {
    event.preventDefault();
    openModal();
  });

  closeButton?.addEventListener('click', closeModal);

  changeAccountsModal.addEventListener('click', (event) => {
    if (applying) return;
    if (event.target === changeAccountsModal) closeModal();
  });

  if (searchInput) {
    let searchTimeout;
    searchInput.addEventListener('input', (event) => {
      clearTimeout(searchTimeout);
      const query = event.target.value || '';
      searchTimeout = setTimeout(() => {
        renderAccountRows(query);
      }, 150); // debounce account list filter
    });
  }

  if (locationSearchInput) {
    let locationSearchTimeout;
    locationSearchInput.addEventListener('input', (event) => {
      if (!locationStepCompany) return;
      clearTimeout(locationSearchTimeout);
      const query = event.target.value || '';
      locationSearchTimeout = setTimeout(() => {
        renderLocationRows(locationStepCompany, query);
      }, 150);
    });
  }
});

